import React, { useState } from "react";
import { ChevronDown, ChevronUp, Target, Eye, EyeOff } from "lucide-react";
import { colors } from "../../../constants/colors";

const UserPlanSection = ({ uid }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [userPlanData, setUserPlanData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchUserPlan = async () => {
    setLoading(true);
    setError(null);
    try {
      let token = sessionStorage.getItem("adminToken");
      if (!token) {
        token = localStorage.getItem("authToken");
      }
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admin/users/${uid}/user-plan`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user plan");
      }

      const data = await response.json();
      setUserPlanData(data.userPlan);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching user plan:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isExpanded && !userPlanData) {
      fetchUserPlan();
    }
    setIsExpanded(!isExpanded);
  };

  const renderComplexValue = (key, value) => {
    // Handle milestones array
    if (key === "milestones" && Array.isArray(value)) {
      return (
        <div>
          {value.map((milestone, index) => (
            <div
              key={index}
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                padding: "0.75rem",
                marginBottom: "0.5rem",
                border: "1px solid #e9ecef",
              }}
            >
              <div
                style={{
                  fontWeight: "600",
                  marginBottom: "0.5rem",
                  color: colors.darkBlue,
                }}
              >
                {milestone.name || `Milestone ${index + 1}`}
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "0.5rem",
                }}
              >
                {milestone.targetWeight && (
                  <div>
                    <span style={{ color: colors.darkGray }}>
                      Target Weight:
                    </span>{" "}
                    <strong>{milestone.targetWeight}kg</strong>
                  </div>
                )}
                {milestone.targetDate && (
                  <div>
                    <span style={{ color: colors.darkGray }}>Target Date:</span>{" "}
                    <strong>{milestone.targetDate}</strong>
                  </div>
                )}
                {milestone.progressPercentage !== undefined && (
                  <div>
                    <span style={{ color: colors.darkGray }}>Progress:</span>{" "}
                    <strong>{milestone.progressPercentage}%</strong>
                  </div>
                )}
                {milestone.isCompleted !== undefined && (
                  <div>
                    <span style={{ color: colors.darkGray }}>Status:</span>
                    <strong
                      style={{
                        color: milestone.isCompleted ? "#059669" : "#dc2626",
                      }}
                    >
                      {milestone.isCompleted ? "Completed" : "In Progress"}
                    </strong>
                  </div>
                )}
              </div>
              {milestone.phaseDescription && (
                <div
                  style={{
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    color: colors.darkGray,
                    marginTop: "0.5rem",
                    padding: "0.5rem",
                    backgroundColor: "#fff",
                    borderRadius: "4px",
                  }}
                >
                  {milestone.phaseDescription}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Handle aiNutrition object
    if (key === "aiNutrition" && typeof value === "object" && value !== null) {
      return (
        <div
          style={{
            backgroundColor: "#f0f9ff",
            borderRadius: "6px",
            padding: "0.75rem",
            border: "1px solid #bfdbfe",
          }}
        >
          <div
            style={{
              fontWeight: "600",
              marginBottom: "0.5rem",
              color: colors.darkBlue,
            }}
          >
            AI Nutrition Recommendations
          </div>
          <div
            style={{
              fontSize: "0.8rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: "0.5rem",
            }}
          >
            {value.calories && (
              <div>
                <span style={{ color: colors.darkGray }}>Calories:</span>{" "}
                <strong>{value.calories}</strong>
              </div>
            )}
            {value.protein && (
              <div>
                <span style={{ color: colors.darkGray }}>Protein:</span>{" "}
                <strong>{value.protein}g</strong>
              </div>
            )}
            {value.carbohydrates && (
              <div>
                <span style={{ color: colors.darkGray }}>Carbs:</span>{" "}
                <strong>{value.carbohydrates}g</strong>
              </div>
            )}
            {value.fat && (
              <div>
                <span style={{ color: colors.darkGray }}>Fat:</span>{" "}
                <strong>{value.fat}g</strong>
              </div>
            )}
            {value.fiber && (
              <div>
                <span style={{ color: colors.darkGray }}>Fiber:</span>{" "}
                <strong>{value.fiber}g</strong>
              </div>
            )}
            {value.potassium && (
              <div>
                <span style={{ color: colors.darkGray }}>Potassium:</span>{" "}
                <strong>{value.potassium}mg</strong>
              </div>
            )}
            {value.sodium && (
              <div>
                <span style={{ color: colors.darkGray }}>Sodium:</span>{" "}
                <strong>{value.sodium}mg</strong>
              </div>
            )}
            {value.sugars && (
              <div>
                <span style={{ color: colors.darkGray }}>Sugars:</span>{" "}
                <strong>{value.sugars}g</strong>
              </div>
            )}
            {value.idealWeight && (
              <div>
                <span style={{ color: colors.darkGray }}>Ideal Weight:</span>{" "}
                <strong>{value.idealWeight}kg</strong>
              </div>
            )}
            {value.weightScore && (
              <div>
                <span style={{ color: colors.darkGray }}>Weight Score:</span>{" "}
                <strong>{value.weightScore}</strong>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Handle goal reach estimation object
    if (
      (key === "goalReachEstimation" || key === "estimations") &&
      typeof value === "object" &&
      value !== null
    ) {
      return (
        <div
          style={{
            backgroundColor: "#f0fdf4",
            borderRadius: "6px",
            padding: "0.75rem",
            border: "1px solid #bbf7d0",
          }}
        >
          <div
            style={{
              fontWeight: "600",
              marginBottom: "0.5rem",
              color: colors.darkGreen,
            }}
          >
            Goal Reach Estimation
          </div>
          <div
            style={{
              fontSize: "0.8rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: "0.5rem",
            }}
          >
            {value.isGenerated !== undefined && (
              <div>
                <span style={{ color: colors.darkGray }}>AI Generated:</span>
                <strong
                  style={{ color: value.isGenerated ? "#059669" : "#dc2626" }}
                >
                  {value.isGenerated ? "Yes" : "No"}
                </strong>
              </div>
            )}
            {value.estimatedWeeks && (
              <div>
                <span style={{ color: colors.darkGray }}>Est. Weeks:</span>{" "}
                <strong>{value.estimatedWeeks}</strong>
              </div>
            )}
            {value.estimatedDate && (
              <div>
                <span style={{ color: colors.darkGray }}>Est. Date:</span>{" "}
                <strong>{value.estimatedDate}</strong>
              </div>
            )}
            {value.bmr && (
              <div>
                <span style={{ color: colors.darkGray }}>BMR:</span>{" "}
                <strong>{value.bmr} cal</strong>
              </div>
            )}
            {value.tdee && (
              <div>
                <span style={{ color: colors.darkGray }}>TDEE:</span>{" "}
                <strong>{value.tdee} cal</strong>
              </div>
            )}
            {value.weightDifference && (
              <div>
                <span style={{ color: colors.darkGray }}>Weight Diff:</span>{" "}
                <strong>{value.weightDifference}kg</strong>
              </div>
            )}
            {value.adjustedSpeed && (
              <div>
                <span style={{ color: colors.darkGray }}>Adj. Speed:</span>{" "}
                <strong>{value.adjustedSpeed}</strong>
              </div>
            )}
          </div>
          {value.adherenceAssessment && (
            <div
              style={{
                fontSize: "0.8rem",
                fontStyle: "italic",
                color: colors.darkGray,
                marginTop: "0.5rem",
                padding: "0.5rem",
                backgroundColor: "#fff",
                borderRadius: "4px",
              }}
            >
              <strong>Assessment:</strong> {value.adherenceAssessment}
            </div>
          )}
          {value.reasonForAdjustment && (
            <div
              style={{
                fontSize: "0.8rem",
                fontStyle: "italic",
                color: colors.darkGray,
                marginTop: "0.5rem",
                padding: "0.5rem",
                backgroundColor: "#fff",
                borderRadius: "4px",
              }}
            >
              <strong>Reason:</strong> {value.reasonForAdjustment}
            </div>
          )}
        </div>
      );
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0)
        return <span style={{ color: colors.darkGray }}>None</span>;
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem" }}>
          {value.map((item, index) => (
            <span
              key={index}
              style={{
                backgroundColor: colors.lightBlue,
                color: colors.darkBlue,
                padding: "0.25rem 0.5rem",
                borderRadius: "12px",
                fontSize: "0.8rem",
                fontWeight: "500",
              }}
            >
              {typeof item === "object"
                ? JSON.stringify(item)
                : item.toString()}
            </span>
          ))}
        </div>
      );
    }

    // Handle other objects
    if (typeof value === "object" && value !== null) {
      return (
        <div
          style={{
            backgroundColor: "#fafafa",
            borderRadius: "6px",
            padding: "0.75rem",
            border: "1px solid #e5e5e5",
          }}
        >
          <div style={{ fontSize: "0.8rem", display: "grid", gap: "0.5rem" }}>
            {Object.entries(value).map(([subKey, subValue]) => (
              <div
                key={subKey}
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <span style={{ color: colors.darkGray }}>{subKey}:</span>
                <strong>{formatValue(subValue)}</strong>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return formatValue(value);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "Not set";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return value.toString();
  };

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    padding: "0.5rem 0",
  };

  const titleStyle = {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: colors.darkBlue,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  };

  const detailCardStyle = {
    backgroundColor: colors.background,
    borderRadius: "8px",
    padding: "1rem",
    marginBottom: "1rem",
    border: `1px solid ${colors.lightGray}`,
  };

  const labelStyle = {
    fontSize: "0.875rem",
    fontWeight: "600",
    color: colors.darkGray,
    marginBottom: "0.5rem",
    textTransform: "capitalize",
  };

  const valueStyle = {
    fontSize: "1rem",
    color: colors.text.primary,
    lineHeight: "1.5",
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle} onClick={handleToggle}>
        <div style={titleStyle}>
          <Target size={20} />
          User Plan
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {isExpanded ? (
            <EyeOff size={16} color={colors.darkGray} />
          ) : (
            <Eye size={16} color={colors.darkGray} />
          )}
          {isExpanded ? (
            <ChevronUp size={20} color={colors.darkGray} />
          ) : (
            <ChevronDown size={20} color={colors.darkGray} />
          )}
        </div>
      </div>

      {isExpanded && (
        <div style={{ marginTop: "1rem" }}>
          {/* Loading State */}
          {loading && (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: colors.darkGray,
              }}
            >
              Loading user plan...
            </div>
          )}

          {/* Error State */}
          {error && (
            <div
              style={{
                backgroundColor: "#fee",
                color: "#c33",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
                border: "1px solid #fcc",
              }}
            >
              Error: {error}
            </div>
          )}

          {/* User Plan Data */}
          {userPlanData && !loading && (
            <div>
              <div
                style={{
                  marginBottom: "1.5rem",
                  padding: "1rem",
                  backgroundColor: colors.lightBlue,
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    color: colors.darkBlue,
                    fontSize: "1.1rem",
                    fontWeight: "600",
                  }}
                >
                  User Plan Configuration
                </h3>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "1rem",
                }}
              >
                {Object.entries(userPlanData).map(([key, value]) => (
                  <div key={key} style={detailCardStyle}>
                    <div style={labelStyle}>
                      {key
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </div>
                    <div style={valueStyle}>
                      {renderComplexValue(key, value)}
                    </div>
                  </div>
                ))}
              </div>

              {Object.keys(userPlanData).length === 0 && (
                <div
                  style={{
                    marginTop: "1.5rem",
                    padding: "2rem",
                    textAlign: "center",
                    color: colors.darkGray,
                    backgroundColor: colors.lightGray,
                    borderRadius: "8px",
                  }}
                >
                  <Target
                    size={32}
                    color={colors.darkGray}
                    style={{ marginBottom: "0.5rem" }}
                  />
                  <div>No user plan data available</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UserPlanSection;
